import { Injectable, inject } from "@angular/core";
import { Constants } from "@config/constants";
import { IAppToken } from "@modals/api.modal";
import { HttpService } from "@services/http.service";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AppTokenResolverService {
  private _httpService = inject(HttpService);
  private _cache = new Map<string, string>();

  resolve$(appToken?: string, appName?: string): Observable<string | null> {
    const preset = appToken?.trim();
    if (preset) {
      return of(preset);
    }

    const cacheKey = appName?.trim().toLowerCase() || "__default__";
    const cached = this._cache.get(cacheKey);
    if (cached) {
      return of(cached);
    }

    return this.fetchFromGetAll$(appName).pipe(
      map((token) => {
        if (token) {
          this._cache.set(cacheKey, token);
        }
        return token;
      })
    );
  }

  private fetchFromGetAll$(appName?: string): Observable<string | null> {
    return this._httpService
      .get(
        this._httpService.createEndpoint(
          Constants.API_URI_APP_TOKEN_GET_ALL,
          undefined,
          { GetActiveToken: true }
        )
      )
      .pipe(
        map((response: unknown) => this.readToken(response, appName)),
        catchError(() => of(null))
      );
  }

  private readToken(response: unknown, appName?: string): string | null {
    const tokens = this.extractTokens(response);
    if (!tokens.length) {
      return null;
    }

    const normalizedAppName = appName?.trim().toLowerCase();
    if (normalizedAppName) {
      const matched = tokens.find(
        (token) => token.AppName?.trim().toLowerCase() === normalizedAppName
      );
      if (matched?.Token?.trim()) {
        return matched.Token.trim();
      }
    }

    const fallback = tokens.find((token) => !!token.Token?.trim());
    return fallback?.Token?.trim() ?? null;
  }

  private extractTokens(response: unknown): IAppToken[] {
    const res = response as { Data?: IAppToken[]; data?: IAppToken[] };
    const raw = res.Data ?? res.data;
    if (Array.isArray(raw)) {
      return raw;
    }
    return raw ? [raw as IAppToken] : [];
  }
}
